import { useEffect, useState } from "react";
import type { BusinessGraphDiffResultV1, ClarificationRequestV1 } from "@enrein/shared-contracts";
import type { StudioApiClient } from "../api.js";
import { BusinessGraphDiffPanel } from "./BusinessGraphDiffPanel.js";

export type Block1GovernanceJourneyProps = {
  readonly api: StudioApiClient;
  readonly projectId: string;
  readonly canMutate: boolean;
};

type LoadState = "idle" | "loading" | "ready" | "empty" | "error";

/**
 * Block 1 Agency Studio journey surface:
 * candidates/context, clarifications, revision diff, permission/error states.
 * Backend authorization remains authoritative — hidden controls are not security.
 */
export function Block1GovernanceJourney(props: Block1GovernanceJourneyProps) {
  const { api, projectId, canMutate } = props;
  const [state, setState] = useState<LoadState>("idle");
  const [error, setError] = useState<string | null>(null);
  const [clarifications, setClarifications] = useState<ClarificationRequestV1[]>([]);
  const [diff, setDiff] = useState<BusinessGraphDiffResultV1 | null>(null);
  const [diffLoading, setDiffLoading] = useState(false);
  const [fromRevisionId, setFromRevisionId] = useState("");
  const [toRevisionId, setToRevisionId] = useState("");
  const [prompt, setPrompt] = useState("");
  const [busy, setBusy] = useState(false);
  const [nextAction, setNextAction] = useState<string | null>(null);

  async function reload() {
    setState("loading");
    setError(null);
    try {
      const list = await api.listClarifications(projectId);
      setClarifications(list.items);
      setState(list.items.length === 0 ? "empty" : "ready");
    } catch (err) {
      setState("error");
      setError(err instanceof Error ? err.message : "network_error");
      setNextAction("Retry loading clarifications. Check tenant/project access.");
    }
  }

  useEffect(() => {
    void reload();
  }, [projectId]);

  async function onRequestClarification() {
    if (!canMutate) {
      setError("permission_denied");
      setNextAction("Your role cannot request clarification. Ask an operator.");
      return;
    }
    if (prompt.trim().length === 0) {
      setError("validation_error");
      setNextAction("Enter a clarification prompt.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const result = await api.createClarification(projectId, {
        schemaVersion: "clarification-create.v1.0",
        kind: "MISSING_INFORMATION",
        prompt: prompt.trim(),
      });
      if (!result.ok) {
        setError(result.error ?? "request_failed");
        setNextAction(result.nextAction ?? "Fix the request and retry.");
        return;
      }
      setPrompt("");
      setNextAction(result.nextAction ?? "Clarification requested.");
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : "network_error");
      setNextAction("Network failure — retry when connectivity returns.");
    } finally {
      setBusy(false);
    }
  }

  async function onCompare() {
    if (!fromRevisionId.trim() || !toRevisionId.trim()) {
      setError("validation_error");
      setNextAction("Provide both revision ids to compare.");
      return;
    }
    setDiffLoading(true);
    setError(null);
    try {
      const result = await api.getBusinessGraphDiff(projectId, fromRevisionId.trim(), toRevisionId.trim());
      setDiff(result);
      setNextAction(result.identical ? "Revisions are identical." : "Review differences before activation.");
    } catch (err) {
      setDiff(null);
      setError(err instanceof Error ? err.message : "network_error");
      setNextAction("Diff failed — verify revisions belong to this tenant/project.");
    } finally {
      setDiffLoading(false);
    }
  }

  return (
    <section aria-label="Block 1 governance journey" data-testid="block1-governance-journey">
      <h2>Project governance</h2>
      <p>Candidates, clarifications, and revision comparison for this project.</p>

      {state === "loading" ? <p>Loading clarification queue…</p> : null}
      {state === "empty" ? <p>No clarification requests yet.</p> : null}
      {error ? (
        <p role="alert" data-testid="block1-governance-error">
          {error}
        </p>
      ) : null}
      {nextAction ? <p data-testid="block1-governance-next-action">{nextAction}</p> : null}

      <div>
        <h3>Clarification requests</h3>
        <ul>
          {clarifications.map((item) => (
            <li
              key={item.clarificationRequestId}
              data-testid="block1-clarification-item"
              data-clarification-id={item.clarificationRequestId}
              data-clarification-status={item.status}
            >
              <strong>{item.kind}</strong> — {item.status}: {item.prompt}
              {item.responseText ? (
                <span data-testid="block1-clarification-response"> → {item.responseText}</span>
              ) : (
                ""
              )}
              {item.status === "SUBMITTED" && canMutate ? (
                <span>
                  {" "}
                  <button
                    type="button"
                    data-testid="block1-resolve-clarification"
                    data-clarification-id={item.clarificationRequestId}
                    onClick={() =>
                      void (async () => {
                        setBusy(true);
                        try {
                          const result = await api.resolveClarification(projectId, item.clarificationRequestId, {
                            schemaVersion: "clarification-resolve.v1.0",
                            outcome: "RESOLVED",
                            expectedLifecycleVersion: item.lifecycleVersion,
                          });
                          if (!result.ok) {
                            setError(result.error ?? "resolve_failed");
                            setNextAction(result.nextAction ?? "Reload and retry resolve.");
                            return;
                          }
                          setNextAction(result.nextAction ?? "Clarification resolved as candidate evidence.");
                          await reload();
                        } catch (err) {
                          setError(err instanceof Error ? err.message : "network_error");
                          setNextAction("Resolve failed — retry when connectivity returns.");
                        } finally {
                          setBusy(false);
                        }
                      })()
                    }
                    disabled={busy}
                  >
                    Resolve
                  </button>
                </span>
              ) : null}
            </li>
          ))}
        </ul>
        <label htmlFor="block1-clarification-prompt">Request missing information</label>
        <textarea
          id="block1-clarification-prompt"
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          disabled={busy || !canMutate}
        />
        <button
          type="button"
          data-testid="block1-request-clarification"
          onClick={() => void onRequestClarification()}
          disabled={busy || !canMutate}
        >
          Request clarification
        </button>
        {!canMutate ? <p>Clarification controls hidden for your role — server still enforces deny.</p> : null}
      </div>

      <div>
        <h3>Revision comparison</h3>
        <label htmlFor="block1-from-rev">From revision</label>
        <input
          id="block1-from-rev"
          value={fromRevisionId}
          onChange={(e) => setFromRevisionId(e.target.value)}
        />
        <label htmlFor="block1-to-rev">To revision</label>
        <input id="block1-to-rev" value={toRevisionId} onChange={(e) => setToRevisionId(e.target.value)} />
        <button type="button" onClick={() => void onCompare()} disabled={diffLoading}>
          Compare revisions
        </button>
        <BusinessGraphDiffPanel diff={diff} loading={diffLoading} error={null} />
      </div>
    </section>
  );
}
